/**
 * @poc-to-production
 *
 * Submission Readiness — primary FC2 outcome surface (doc package + code).
 */
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import type { SubmissionDocRequirement } from "../data/laCountySubmissionCatalog";
import { LA_COUNTY_SUBMISSION_CATALOG } from "../data/laCountySubmissionCatalog";
import { useAccPackageScan } from "../hooks/useAccPackageScan";
import { useSubmissionUpload } from "../hooks/useSubmissionUpload";
import {
  matchRequirementsToPackage,
  requiredDocSummary,
} from "../lib/matchSubmissionDocs";
import { PRODUCT_ABBR } from "../productBrand";
import { dmRegionFromHub } from "../services/dataManagement/region";
import { useAccBrowseStore } from "../stores/accBrowseStore";
import { useDocumentViewerStore } from "../stores/documentViewerStore";
import { useEngineStore } from "../stores/engineStore";
import { usePackageInventoryStore } from "../stores/packageInventoryStore";
import { useCurrentProject } from "../stores/projectStore";
import { useSubmissionUploadStore } from "../stores/submissionUploadStore";
import {
  filterViolationsForDisplay,
  partitionViolationsByOutcome,
} from "../stores/violationFilters";
import { useViolationsStore } from "../stores/violationsStore";
import { Card } from "./Card";
import { Fc2RefreshButton } from "./Fc2RefreshButton";
import { UploadProgressRing } from "./UploadProgressRing";

export function SubmissionReadinessPanel() {
  const accMode = !isAuthDisabled();
  const demoProject = useCurrentProject();
  const hub = useAccBrowseStore(s => s.hub);
  const accProject = useAccBrowseStore(s => s.project);

  const scanEnabled = accMode && !!hub && !!accProject;
  const { runScan } = useAccPackageScan(scanEnabled);
  const {
    uploadForRequirement,
    removeMatchedPdf,
    activeRequirementId,
    progress: uploadProgress,
    justCompletedIds,
    lastError: uploadError,
    busy: uploadBusy,
  } = useSubmissionUpload(runScan);
  const clearJustCompleted = useSubmissionUploadStore(
    s => s.clearJustCompleted
  );
  const pendingFileActionRef = useRef<{
    requirement: SubmissionDocRequirement;
    mode: "upload" | "replace";
    replaceItemId?: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const inventoryFiles = usePackageInventoryStore(s => s.files);
  const scanStatus = usePackageInventoryStore(s => s.status);
  const scanError = usePackageInventoryStore(s => s.error);
  const foldersVisited = usePackageInventoryStore(s => s.foldersVisited);

  const violations = useViolationsStore(s => s.violations);
  const engineLoaded = useEngineStore(s => s.loaded);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const openDocument = useDocumentViewerStore(s => s.openDocument);

  const catalog = LA_COUNTY_SUBMISSION_CATALOG;

  const siteLine = useMemo(() => {
    if (accMode) {
      if (!accProject) {
        return "Select hub and ACC project above — we scan the whole project for submission files.";
      }
      return `${accProject.name} (${hub?.name ?? "ACC"}) — street address from ACC project settings is coming next; jurisdiction is inferred from project context.`;
    }
    const loc = demoProject.location;
    const cityState = `${loc.city}, ${loc.stateCode} ${loc.postalCode}`.trim();
    return `${demoProject.projectName} · ${cityState}`;
  }, [accMode, accProject, hub?.name, demoProject]);

  const docMatches = useMemo(
    () => matchRequirementsToPackage(catalog.requirements, inventoryFiles),
    [catalog.requirements, inventoryFiles]
  );
  const docStats = useMemo(() => requiredDocSummary(docMatches), [docMatches]);

  useEffect(() => {
    for (const row of docMatches) {
      if (
        row.status === "present" &&
        justCompletedIds.includes(row.requirement.id)
      ) {
        clearJustCompleted(row.requirement.id);
      }
    }
  }, [docMatches, justCompletedIds, clearJustCompleted]);

  const displayedViolations = useMemo(
    () => filterViolationsForDisplay(violations, { enabledJurisdictions }),
    [violations, enabledJurisdictions]
  );
  const outcomeGroups = useMemo(
    () => partitionViolationsByOutcome(displayedViolations),
    [displayedViolations]
  );
  const confirmedCount =
    outcomeGroups.confirmed.length + outcomeGroups.userVerified.length;
  const needsInfoCount = outcomeGroups.needsInfo.length;
  const codeOpenCount = confirmedCount + needsInfoCount;

  const docsReady =
    !scanEnabled ||
    (docStats.missingRequired === 0 && inventoryFiles.length > 0);
  const codeReady = engineLoaded && codeOpenCount === 0;
  const ready = docsReady && codeReady && (!accMode || scanStatus === "done");

  if (accMode && !accProject) {
    return (
      <Card
        title="Submission Readiness"
        accent="#6E56CF"
        defaultOpen
        meta="Select project"
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--fc2-text-dim)",
          }}
        >
          Choose an ACC hub and project in the panel above. {PRODUCT_ABBR} will
          scan all folders in that project for Revit models and PDFs, then show
          document and code readiness here.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="Submission Readiness"
      accent="#6E56CF"
      defaultOpen
      meta={ready ? "Ready to review" : "Action needed"}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: ready ? "#78c896" : "#f0a060",
          marginBottom: 8,
        }}
      >
        {ready ? "Ready for plan-check review" : "Not ready to submit"}
      </div>

      <Section label="Project location">
        <div style={{ fontSize: 11, lineHeight: 1.45 }}>{siteLine}</div>
      </Section>

      <Section label="Permit submitted to">
        <div style={{ fontSize: 11, lineHeight: 1.45 }}>
          {catalog.permittingAuthorityLabel}
        </div>
        <a
          href={catalog.officialSourceUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 11,
            color: "#7fb3d5",
            display: "block",
            marginTop: 4,
          }}
        >
          {catalog.officialSourceLabel} ↗
        </a>
        {catalog.residentialChecklistPdfUrl && (
          <a
            href={catalog.residentialChecklistPdfUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 11,
              color: "#7fb3d5",
              display: "block",
              marginTop: 2,
            }}
          >
            {catalog.residentialChecklistPdfLabel} ↗
          </a>
        )}
      </Section>

      <Section label="Building code basis">
        <div style={{ fontSize: 11, lineHeight: 1.45 }}>
          {catalog.codeBasisHint}
        </div>
      </Section>

      <Section label="Submission package">
        {accMode ? (
          <>
            <div
              style={{
                fontSize: 11,
                color: "var(--fc2-text-dim)",
                lineHeight: 1.45,
              }}
            >
              Scanning all folders in this ACC project for .rvt and .pdf files.
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--fc2-text-dim)",
                marginTop: 6,
              }}
            >
              {scanStatus === "scanning" && "Scanning project files…"}
              {scanStatus === "done" &&
                `${inventoryFiles.length} file(s) in ${foldersVisited} folder(s)`}
              {scanStatus === "error" && scanError}
            </div>
            <Fc2RefreshButton
              loading={scanStatus === "scanning"}
              disabled={uploadBusy}
              style={{ ...btnStyle, marginTop: 6 }}
              onClick={() => void runScan()}
            >
              Refresh scan
            </Fc2RefreshButton>
            <div
              style={{
                fontSize: 10,
                color: "var(--fc2-text-dim)",
                marginTop: 6,
                lineHeight: 1.4,
              }}
            >
              Uploads use AIS to check PDF type, then write to the browsed ACC
              folder (or Project Files). Sign out and back in once after scope
              updates if upload returns 403.
            </div>
            {uploadError && (
              <div style={{ fontSize: 10, color: "#f87171", marginTop: 4 }}>
                {uploadError}
              </div>
            )}
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 11, color: "var(--fc2-text-dim)" }}>
            Demo mode — sign in with APS to scan ACC PDFs. Code checks use the
            loaded model.
          </p>
        )}
      </Section>

      <Section
        label={`Required documents (${docStats.present}/${docStats.required})`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={e => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            const pending = pendingFileActionRef.current;
            pendingFileActionRef.current = null;
            input.value = "";
            if (!file || !pending) return;
            void uploadForRequirement(pending.requirement, file, {
              replaceItemId:
                pending.mode === "replace" ? pending.replaceItemId : undefined,
            }).catch(() => undefined);
          }}
        />
        <ul
          style={{ margin: 0, paddingLeft: 16, fontSize: 11, lineHeight: 1.5 }}
        >
          {docMatches.map(row => {
            const uploading = activeRequirementId === row.requirement.id;
            const showTick =
              row.status === "present" ||
              justCompletedIds.includes(row.requirement.id);
            return (
              <li key={row.requirement.id} style={{ marginBottom: 4 }}>
                {uploading ? (
                  <UploadProgressRing percent={uploadProgress} />
                ) : (
                  <span
                    style={{
                      color: showTick ? "#78c896" : "#f87171",
                      fontWeight: 600,
                    }}
                  >
                    {showTick ? "✓" : "○"}
                  </span>
                )}{" "}
                {row.requirement.label}
                {!row.requirement.required && (
                  <span style={{ color: "var(--fc2-text-dim)" }}>
                    {" "}
                    (optional)
                  </span>
                )}
                {row.matchedFiles[0] && accMode && accProject && (
                  <>
                    {" "}
                    <button
                      type="button"
                      style={linkBtn}
                      onClick={() =>
                        openDocument({
                          projectId: accProject.id,
                          itemId: row.matchedFiles[0].itemId,
                          fileName: row.matchedFiles[0].fileName,
                          hubRegion: dmRegionFromHub(hub?.region),
                        })
                      }
                    >
                      View PDF
                    </button>{" "}
                    <button
                      type="button"
                      style={linkBtn}
                      disabled={uploadBusy}
                      onClick={() => {
                        pendingFileActionRef.current = {
                          requirement: row.requirement,
                          mode: "replace",
                          replaceItemId: row.matchedFiles[0].itemId,
                        };
                        fileInputRef.current?.click();
                      }}
                    >
                      Replace
                    </button>{" "}
                    <button
                      type="button"
                      style={dangerLinkBtn}
                      disabled={uploadBusy}
                      onClick={() => {
                        const match = row.matchedFiles[0];
                        const ok = window.confirm(
                          `Remove "${match.fileName}" from this ACC project? It will move to ACC Deleted Items.`
                        );
                        if (!ok) return;
                        void removeMatchedPdf(
                          row.requirement,
                          match.itemId,
                          match.fileName
                        ).catch(() => undefined);
                      }}
                    >
                      Remove
                    </button>
                  </>
                )}
                {accMode &&
                  accProject &&
                  row.status !== "present" &&
                  row.requirement.required && (
                    <>
                      {" "}
                      <button
                        type="button"
                        style={linkBtn}
                        disabled={uploadBusy}
                        onClick={() => {
                          pendingFileActionRef.current = {
                            requirement: row.requirement,
                            mode: "upload",
                          };
                          fileInputRef.current?.click();
                        }}
                      >
                        Upload PDF
                      </button>
                    </>
                  )}
              </li>
            );
          })}
        </ul>
      </Section>

      <Section label="Code compliance">
        <div style={{ fontSize: 11, lineHeight: 1.5 }}>
          {!engineLoaded ? (
            <span style={{ color: "var(--fc2-text-dim)" }}>
              Open a Revit model to run code checks.
            </span>
          ) : codeOpenCount === 0 ? (
            <span style={{ color: "var(--fc2-text-dim)" }}>
              No open code findings on evaluated elements — see Violations for
              detail.
            </span>
          ) : (
            <>
              <div style={{ color: "#f0a060" }}>
                {codeOpenCount} open code finding
                {codeOpenCount === 1 ? "" : "s"} — see Violations below
              </div>
              <div
                style={{
                  color: "var(--fc2-text-dim)",
                  marginTop: 4,
                  fontSize: 10,
                }}
              >
                {confirmedCount > 0 && (
                  <span>
                    {confirmedCount} confirmed violation
                    {confirmedCount === 1 ? "" : "s"}
                  </span>
                )}
                {confirmedCount > 0 && needsInfoCount > 0 && " · "}
                {needsInfoCount > 0 && (
                  <span>{needsInfoCount} need evidence or user input</span>
                )}
              </div>
            </>
          )}
        </div>
      </Section>
    </Card>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: ComponentChildren;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "var(--fc2-text-dim)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const btnStyle: Record<string, string | number> = {
  fontSize: 10,
  padding: "4px 8px",
  borderRadius: 4,
  border: "1px solid var(--fc2-border)",
  background: "rgba(255,255,255,0.06)",
  color: "inherit",
  cursor: "pointer",
};

const linkBtn: Record<string, string | number> = {
  fontSize: 10,
  padding: 0,
  border: "none",
  background: "none",
  color: "#7fb3d5",
  cursor: "pointer",
  textDecoration: "underline",
};

const dangerLinkBtn: Record<string, string | number> = {
  ...linkBtn,
  color: "#f0a0a0",
};
